import { Navigate, useLocation } from "react-router";

import { useAuth, type Role } from "@/contexts/AuthContext";
import { homeForMember, needsAccessResolution, ownTeachingCourseAddress } from "@/navigation/member-routing";
import { ACCESS_PENDING_ROUTE } from "@/navigation/routes";

import { RouteSkeleton } from "./RouteSkeleton";
import { SignInRedirect } from "@/components/auth/SignInReturn";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Roles allowed here; omit to allow any authenticated member. */
  roles?: Role[];
  /** Relationship-derived workspace required here. */
  workspace?: "registrar" | "teaching" | "learning";
}

/**
 * Gate a route on an authenticated session + (optionally) the new-vocab role.
 * Unauthenticated → /sign-in; wrong role → the caller's own landing, or for a
 * teaching course address, the same course in the caller's own teaching shell.
 */
export function ProtectedRoute({ children, roles, workspace }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, me, membershipStatus, role } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <RouteSkeleton rail />;
  }

  // Checked before the unauthenticated redirect: `isAuthenticated` is false for
  // an authenticated non-member too, and sending that person to /sign-in is the
  // silent dead end this guard exists to avoid.
  if (needsAccessResolution(membershipStatus)) {
    return <SignInRedirect to={ACCESS_PENDING_ROUTE} returnTo={location.pathname + location.search + location.hash} />;
  }

  if (!isAuthenticated) {
    return <SignInRedirect to="/sign-in" returnTo={location.pathname + location.search + location.hash} />;
  }

  if (roles && role && !roles.includes(role)) {
    // A teaching course in the other teaching shell keeps its course and view in this member's own.
    const own = ownTeachingCourseAddress(role, me?.workspaces, location.pathname, location.search, location.hash);
    return <Navigate to={own ?? homeForMember(role, me?.workspaces)} replace />;
  }

  const workspaceAvailable = workspace
    ? me?.workspaces?.[workspace]?.available === true
    : true;
  if (!workspaceAvailable) {
    return <Navigate to={homeForMember(role, me?.workspaces)} replace />;
  }

  return <>{children}</>;
}
